function buildMainCartItemFromForm(form) {
  const fd = new FormData(form);
  const id = parseInt(String(fd.get('id')), 10);
  const quantity = Math.max(1, parseInt(String(fd.get('quantity') || '1'), 10) || 1);
  const item = { id, quantity };

  const properties = {};
  for (const [key, value] of fd.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match) {
      properties[match[1]] = value;
    }
  }
  if (Object.keys(properties).length > 0) {
    item.properties = properties;
  }

  const sellingPlan = fd.get('selling_plan');
  if (sellingPlan) {
    const n = parseInt(String(sellingPlan), 10);
    item.selling_plan = Number.isNaN(n) ? sellingPlan : n;
  }

  return item;
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        const productInfoEl = this.closest('product-info');
        if (productInfoEl) {
          this._productInfoEl = productInfoEl;
          this._onCartAddonChange = (e) => {
            const t = e.target;
            if (!(t instanceof HTMLInputElement) || !t.classList.contains('gs-cart-addon__input') || !t.checked) {
              return;
            }
            productInfoEl.querySelectorAll('.gs-product-checkbox-option__input:checked').forEach((el) => {
              el.checked = false;
            });
          };
          productInfoEl.addEventListener('change', this._onCartAddonChange);
        }
      }

      disconnectedCallback() {
        if (this._productInfoEl && this._onCartAddonChange) {
          this._productInfoEl.removeEventListener('change', this._onCartAddonChange);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const mainVariantId = this.form.querySelector('[name=id]').value;
        const sectionId = this.dataset.sectionId;
        const productInfoEl = document.getElementById(`ProductInfo-${sectionId}`);
        const addonInputs = productInfoEl
          ? [...productInfoEl.querySelectorAll('input[type="checkbox"][data-cart-addon-variant]:checked')]
          : [];
        const upsellProduct = document.querySelector('#upsell-product-item');

        let body;

        if (addonInputs.length > 0) {
          config.headers['Content-Type'] = 'application/json';

          const mainItem = buildMainCartItemFromForm(this.form);
          const items = [mainItem];
          const seen = new Set([mainItem.id]);

          if (upsellProduct && upsellProduct.checked) {
            const upsId = parseInt(String(upsellProduct.value), 10);
            if (!Number.isNaN(upsId) && !seen.has(upsId)) {
              items.push({ id: upsId, quantity: 1 });
              seen.add(upsId);
            }
          }

          addonInputs.forEach((input) => {
            const vid = parseInt(String(input.getAttribute('data-cart-addon-variant') || input.value), 10);
            if (!Number.isNaN(vid) && !seen.has(vid)) {
              items.push({ id: vid, quantity: 1 });
              seen.add(vid);
            }
          });

          const payload = { items };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];

          let formData = new FormData();
          if (upsellProduct && upsellProduct.checked) {
            formData.append('id[]', this.form.querySelector('[name=id]').value);
            formData.append('id[]', upsellProduct.value);
            formData.append('quantity', 1);
          } else {
            formData = new FormData(this.form);
          }

          if (this.cart) {
            formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          body = formData;
        }

        config.body = body;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: mainVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: mainVariantId,
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
